import {addYears, differenceInYears} from 'date-fns/esm';
import {
  CAPITAL_ID,
  CAPITAL_RATE_ID,
  GOAL_CAPITAL_MAX,
  GOAL_CAPITAL_MIN,
  INCOME_ID,
  INCOME_RATE_ID,
  MAX_MOMENT_YEAR,
  OBJECTIVE_CAPITAL,
  OBJECTIVE_INCOME,
  OWNER_PRIMARY,
  OWNER_SECONDARY,
  OWNER_TRUST,
  PURPOSE_RETIREMENT,
  RATE_DEFAULT,
  RATE_MAX,
  RATE_MIN,
} from './constants';
import {UPDATES} from '../../../shared/UPDATES';
import {GoalFormValues, calculateAge} from './utils';
import {newUTCDate} from '../../../utils/newUTCDate';
import {AccountDetailsFragment} from '../../../schema-types/AccountDetailsFragment';
import {SetFieldValueWithSourceKey} from '../../../components/AutoForm/useAutoForm';

export const targetAgeBusinessRules = (values: GoalFormValues) =>
  (values?.ownershipRadio === OWNER_PRIMARY ||
    values?.ownershipRadio === OWNER_SECONDARY ||
    values?.ownershipRadio === OWNER_TRUST) &&
  (values?.planningObjective === OBJECTIVE_INCOME ||
    values?.planningObjective === OBJECTIVE_CAPITAL) &&
  values.planningObjectivePurpose === PURPOSE_RETIREMENT;

export const clampYearsInTheFuture = (dateToday: Date, yearsInTheFuture: number) => {
  const thisYear = dateToday.getUTCFullYear();
  return thisYear + yearsInTheFuture > MAX_MOMENT_YEAR
    ? MAX_MOMENT_YEAR - thisYear
    : yearsInTheFuture;
};

export const getFutureDate = (date: Date, yearsInTheFuture: number) =>
  new Date(
    date.getUTCFullYear() + clampYearsInTheFuture(date, yearsInTheFuture),
    date.getUTCMonth(),
    date.getUTCDate()
  );

export const getDrawdownFromCapital = (capital: number, rate: number) =>
  Number(((capital / 100) * rate).toFixed(2));

export const getCapitalFromDrawdown = (rate: number, capital: number) =>
  Number(((rate / capital) * 100).toFixed(2));

export const getGoalAmountOutput = (
  isIncome: boolean,
  values: GoalFormValues,
  overrideRate?: number | string
) => {
  const {targetIncome, targetCapital, drawdownRate} = values;
  return isIncome
    ? getCapitalFromDrawdown(targetIncome, Number(overrideRate) || drawdownRate)
    : getDrawdownFromCapital(targetCapital, Number(overrideRate) || drawdownRate);
};

export const getTargetFields = (
  isIncome: boolean
):
  | {
      target: typeof INCOME_ID;
      rate: typeof INCOME_RATE_ID;
      output: typeof CAPITAL_ID;
    }
  | {
      target: typeof CAPITAL_ID;
      rate: typeof CAPITAL_RATE_ID;
      output: typeof INCOME_ID;
    } => {
  if (isIncome) {
    return {
      target: INCOME_ID,
      rate: INCOME_RATE_ID,
      output: CAPITAL_ID,
    };
  }

  return {
    target: CAPITAL_ID,
    rate: CAPITAL_RATE_ID,
    output: INCOME_ID,
  };
};

export const getClientAge = (
  values: GoalFormValues,
  account: AccountDetailsFragment | undefined
) => {
  const dob =
    values.ownershipRadio === OWNER_PRIMARY || values.ownershipRadio === OWNER_TRUST
      ? account?.primaryContact?.dob
      : account?.secondaryContact?.dob;
  return calculateAge(dob);
};

export const setGoalAmountOutput = ({
  values,
  target,
  setFieldValue,
  rate,
  output,
}: {
  target: 'targetIncome' | 'targetCapital';
  rate: 'incomeRate' | 'capitalRate';
  output: 'targetIncome' | 'targetCapital';
  values: GoalFormValues;
  setFieldValue: SetFieldValueWithSourceKey<GoalFormValues>;
}) => {
  if (
    values[target] === undefined ||
    Number.isNaN(values[target]) ||
    values[target] < GOAL_CAPITAL_MIN
  ) {
    setFieldValue(UPDATES._1107, target, GOAL_CAPITAL_MIN);
  } else if (values[target] > GOAL_CAPITAL_MAX && target === 'targetCapital') {
    setFieldValue(UPDATES._1107, target, GOAL_CAPITAL_MAX);
  }

  if (values[rate] === undefined || Number.isNaN(values[rate])) {
    setFieldValue(UPDATES._1107, rate, RATE_DEFAULT);
  } else if (Number(values[rate]) < RATE_MIN) {
    setFieldValue(UPDATES._1107, rate, RATE_MIN);
  } else if (Number(values[rate]) > RATE_MAX) {
    setFieldValue(UPDATES._1107, rate, RATE_MAX);
  }

  let value = getGoalAmountOutput(target === 'targetIncome', values, values[rate]);
  if (Number.isNaN(value) || value < GOAL_CAPITAL_MIN) {
    value = GOAL_CAPITAL_MIN;
  } else if (value > GOAL_CAPITAL_MAX && target !== 'targetCapital') {
    value = GOAL_CAPITAL_MAX;
    const fixedIncome = getDrawdownFromCapital(value, values[rate]);
    setFieldValue(UPDATES._1107, 'targetIncome', fixedIncome);
  }
  setFieldValue(UPDATES._1107, output, value);
};

export const onUpdateTargetYearsFactory = ({
  values,
  account,
  setFieldValue,
}: {
  values: GoalFormValues;
  account: AccountDetailsFragment | undefined;
  setFieldValue: SetFieldValueWithSourceKey<GoalFormValues>;
}) => {
  return (_targetYears?: number | string) => {
    if (!_targetYears) {
      return;
    }
    try {
      const currentYear = new Date().getUTCFullYear();
      const targetYears = Number(_targetYears);
      // do something
      const clientAge = getClientAge(values, account);
      // keep the currently selected date
      const dateToday = new Date(
        currentYear,
        new Date(values.targetEndDate).getUTCMonth() || newUTCDate().getUTCMonth(),
        new Date(values.targetEndDate).getUTCDate() || newUTCDate().getUTCDate()
      );

      setFieldValue(
        UPDATES._1108,
        'targetEndDate',
        getFutureDate(dateToday, targetYears).toISOString(),
        true
      );
      setFieldValue(UPDATES._1109, 'targetAge', clientAge + targetYears, true);
    } catch (err) {
      // do nothing
    }
  };
};

export const onUpdateTargetAgeFactory = ({
  values,
  account,
  setFieldValue,
}: {
  values: GoalFormValues;
  account: AccountDetailsFragment | undefined;
  setFieldValue: SetFieldValueWithSourceKey<GoalFormValues>;
}) => {
  return (_targetAge?: number | string) => {
    if (!_targetAge) {
      return;
    }
    try {
      const currentYear = new Date().getUTCFullYear();
      const targetAge = Number(_targetAge);
      // do something
      const clientAge = getClientAge(values, account);
      const dateToday = new Date(
        currentYear,
        new Date(values.targetEndDate).getUTCMonth() || newUTCDate().getUTCMonth(),
        new Date(values.targetEndDate).getUTCDate() || newUTCDate().getUTCDate()
      );

      const targetYears = targetAge - clientAge;
      const futureDate = new Date(dateToday);
      const adjustedYears = addYears(futureDate, targetYears);
      setFieldValue(UPDATES._1108, 'targetEndDate', adjustedYears, true);
      setFieldValue(UPDATES._1110, 'targetYears', targetYears, true);
    } catch (err) {
      // do nothing
    }
  };
};

export const onUpdateTargetDateFactory = ({
  values,
  account,
  setFieldValue,
}: {
  values: GoalFormValues;
  account: AccountDetailsFragment | undefined;
  setFieldValue: SetFieldValueWithSourceKey<GoalFormValues>;
}) => {
  return (_targetDate?: Date | string) => {
    if (!_targetDate) {
      return;
    }
    try {
      const dateToday = newUTCDate();
      const clientAge = getClientAge(values, account);
      const targetDate = newUTCDate(_targetDate);
      const targetYears = differenceInYears(targetDate, dateToday);
      const adjustedYears = clampYearsInTheFuture(dateToday, targetYears);
      setFieldValue(UPDATES._1110, 'targetYears', adjustedYears, true);
      setFieldValue(UPDATES._1109, 'targetAge', clientAge + adjustedYears, true);
    } catch (err) {
      // do nothing
    }
  };
};
